"use client";

import Link from "next/link";
import type { ComponentPropsWithRef, MouseEvent } from "react";

import { type InternalSource, rememberInternalSource } from "@/lib/analytics/internal-source";

type InternalSourceLinkProps = Omit<ComponentPropsWithRef<typeof Link>, "href"> & {
	/** Clean target URL: never add `src`/`src_slug` to it, crawlers would index the variant. */
	href: string;
	source: InternalSource;
	detail?: string | null;
};

/**
 * The click opens the target in this tab, the same test `next/link` applies before it navigates
 * client-side. A new tab or window has its own sessionStorage, and a handoff left in this tab
 * would attribute a later, unrelated visit of the same page.
 */
function opensInThisTab(event: MouseEvent<HTMLAnchorElement>): boolean {
	const target = event.currentTarget.getAttribute("target");
	return (
		!event.defaultPrevented &&
		event.button === 0 &&
		!event.metaKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		!event.altKey &&
		(!target || target === "_self")
	);
}

/**
 * `next/link` to a product page that attributes the visit to the surface it was clicked on
 * (calculator, search) without putting the attribution into the URL: a plain click hands it to
 * the target page through `rememberInternalSource`. The navigation is never prevented. The
 * caller's `onClick` runs first, as in `next/link`; when it prevents the default, nothing is
 * stored. Modifier, middle and new-tab clicks store nothing (see `opensInThisTab`).
 */
export function InternalSourceLink({ href, source, detail, onClick, ...props }: InternalSourceLinkProps) {
	return (
		<Link
			{...props}
			href={href}
			onClick={(event) => {
				onClick?.(event);
				if (opensInThisTab(event)) rememberInternalSource(href, source, detail);
			}}
		/>
	);
}
